/* eslint-disable */
// @ts-nocheck

import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import APIServices from '@/api/APIServices'
import APIServicesV2 from '@/api/APIServicesV2'
import { getUsers } from '@/api/usersAPI'
import { AsyncSelectForm } from '@/components/forms/AsyncSelectForm'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { Label } from '@/components/ui/label'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'

import { IContainer } from '../../../interfaces/interfaces'

export default function AseguramientoContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, setValue, getValues, watch } = useFormWizzard

  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const data = useContainerWizzardSteps((state) => state.data)
  const queryClient = useQueryClient()

  // Local state to preserve transportedBy value
  const [localTransportedBy, setLocalTransportedBy] = useState<any>(null)
  // Cache for vehicles to avoid additional API calls
  const [vehiclesCache, setVehiclesCache] = useState<any[]>([])
  // Cache for users to avoid additional API calls
  const [usersCache, setUsersCache] = useState<any[]>([])

  // Query for container logistics data
  const { data: containerLogistics, isLoading } = useQuery({
    queryKey: ['container_logistics', editContainer?.id],
    queryFn: async () => {
      if (!editContainer?.id) return null

      const response = await APIServicesV2.get(
        `/containers/${editContainer.id}/logistics`
      )

      return response.data
    },
    enabled: isEditing && !!editContainer?.id,
    staleTime: 5 * 60 * 1000,
  })

  // Async fetchers for AsyncSelectForm
  const fetchTransportCompanies = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'TRANSPORTER',
        },
      })

      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      return []
    }
  }

  const fetchStorageCompanies = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'STORAGE',
        },
      })

      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      return []
    }
  }

  const fetchVehicles = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/vehicle', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
        },
      })

      const items = response.data?.items || []

      // Cache the vehicles data locally for later use, ensuring IDs are strings
      const vehiclesWithStringIds = items.map((vehicle: any) => ({
        ...vehicle,
        id: String(vehicle.id),
      }))

      // Update cache with new vehicles, avoiding duplicates
      setVehiclesCache((prev) => {
        const existingIds = new Set(prev.map((v) => String(v.id)))
        const newVehicles = vehiclesWithStringIds.filter(
          (v: any) => !existingIds.has(String(v.id))
        )
        return [...prev, ...newVehicles]
      })

      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      return []
    }
  }

  const fetchUsers = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await getUsers({
        page: 1,
        per_page: 10,
        search: search || '',
      })

      const items = response || []

      // Cache the users data locally for later use, ensuring IDs are strings
      const usersWithStringIds = items.map((user: any) => ({
        ...user,
        id: String(user.id),
      }))

      // Update cache with new users, avoiding duplicates
      setUsersCache((prev) => {
        const existingIds = new Set(prev.map((u) => String(u.id)))
        const newUsers = usersWithStringIds.filter(
          (u: any) => !existingIds.has(String(u.id))
        )
        return [...prev, ...newUsers]
      })

      return items.map((user: any) => ({
        id: String(user.id),
        label: `${user.displayName} - ${user.email || ''}`,
      }))
    } catch (error) {
      return []
    }
  }

  const fetchTransportedBy = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'TRANSPORTER',
        },
      })

      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      return []
    }
  }

  // Mutation for updating container logistics
  const updateContainerLogistics = useMutation({
    mutationFn: async (logisticsData: {
      transporterCompanyId?: number | null
      storageCompanyId?: number | null
      vehicleId?: number | null
      transportedById?: number | null
    }) => {
      if (!editContainer?.id) throw new Error('Container ID is required')

      const response = await APIServicesV2.patch(
        `/containers/${editContainer.id}/logistics`,
        logisticsData
      )

      return response.data
    },
    onSuccess: (data) => {
      toast.success('Información de aseguramiento actualizada correctamente')

      queryClient.invalidateQueries({
        queryKey: ['container_logistics', editContainer?.id],
      })

      // Cerrar modal si existe la función global
      if (
        typeof window !== 'undefined' &&
        (window as any).closeContainerModal
      ) {
        ;(window as any).closeContainerModal()
      }

      // Also do a manual fetch to see what was actually saved
      setTimeout(async () => {
        try {
          const verificationResponse = await APIServicesV2.get(
            `/containers/${editContainer?.id}/logistics`
          )
        } catch (error) {}
      }, 1000)
    },
    onError: (error: any) => {
      // DEBUG: MUTATION ERROR - Details: ${JSON.stringify(error)}
      // DEBUG: Error response: ${JSON.stringify(error?.response?.data)}
      // DEBUG: Error status: ${error?.response?.status}
      // DEBUG: END MUTATION ERROR DEBUG

      // DEBUG: Error updating container logistics: ${JSON.stringify(error)}
      toast.error(
        error?.response?.data?.message ||
          'Error al actualizar la información de aseguramiento'
      )
    },
  })

  // Set form values when container logistics are loaded
  useEffect(() => {
    if (containerLogistics) {
      setValue(
        'transporterCompany',
        containerLogistics.transporterCompany?.id
          ? String(containerLogistics.transporterCompany.id)
          : null
      )
      setValue(
        'storageCompany',
        containerLogistics.storageCompany?.id
          ? String(containerLogistics.storageCompany.id)
          : null
      )
      setValue(
        'vehicle',
        containerLogistics.vehicle?.id
          ? String(containerLogistics.vehicle.id)
          : null
      )

      // Para transportedBy también solo necesitamos el ID como string
      setValue(
        'transportedBy',
        containerLogistics.transportedBy?.id
          ? String(containerLogistics.transportedBy.id)
          : null
      )

      // Mantener el estado local para otras funcionalidades si es necesario
      const transportedByValue = containerLogistics.transportedBy
        ? {
            id: containerLogistics.transportedBy.id,
            displayName: containerLogistics.transportedBy.displayName,
            email: containerLogistics.transportedBy.email || '',
          }
        : null

      setLocalTransportedBy(transportedByValue) // Update local state
    }
  }, [containerLogistics, setValue, setLocalTransportedBy])

  // Watch form changes and update data - using a single useEffect to avoid infinite loops
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        // Preserve transportedBy in local state immediately when it changes
        if (name === 'transportedBy') {
          setLocalTransportedBy(value[name])
        }

        updateData({ ...data, [name]: value[name] })
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, updateData, data, setLocalTransportedBy])

  // Additional watch specifically for transportedBy to ensure we always capture changes
  const watchedTransportedBy = watch('transportedBy')
  useEffect(() => {
    if (watchedTransportedBy) {
      setLocalTransportedBy(watchedTransportedBy)
    }
  }, [watchedTransportedBy, setLocalTransportedBy])

  // Watch transporter company to conditionally show vehicle and transportedBy fields
  const transporterCompany = watch('transporterCompany')
  const showVehicleAndTransportedBy =
    transporterCompany &&
    ((typeof transporterCompany === 'string' && transporterCompany !== '') ||
      (typeof transporterCompany === 'object' &&
        transporterCompany !== null &&
        transporterCompany.id))

  // Get vehicle information for display
  const selectedVehicle = watch('vehicle')

  // Function to get vehicle info from local cache
  const getVehicleInfoFromCache = (vehicleId: string | number) => {
    if (!vehicleId || !vehiclesCache.length) return null

    const vehicleIdString = String(vehicleId)
    return (
      vehiclesCache.find((vehicle) => String(vehicle.id) === vehicleIdString) ||
      null
    )
  }

  // Use cache to get vehicle info when vehicle is selected, otherwise fallback to containerLogistics vehicle
  const vehicleInfo = selectedVehicle
    ? getVehicleInfoFromCache(selectedVehicle) || containerLogistics?.vehicle
    : containerLogistics?.vehicle

  // Load initial data for saved values to populate caches
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // Load saved vehicle data if exists
        if (
          containerLogistics?.vehicle?.id &&
          !vehiclesCache.find(
            (v) => String(v.id) === String(containerLogistics.vehicle.id)
          )
        ) {
          setVehiclesCache((prev) => [
            ...prev,
            {
              ...containerLogistics.vehicle,
              id: String(containerLogistics.vehicle.id),
            },
          ])
        }

        // Load saved user data if exists
        if (
          containerLogistics?.transportedBy?.id &&
          !usersCache.find(
            (u) => String(u.id) === String(containerLogistics.transportedBy.id)
          )
        ) {
          setUsersCache((prev) => [
            ...prev,
            {
              ...containerLogistics.transportedBy,
              id: String(containerLogistics.transportedBy.id),
            },
          ])
        }
      } catch (error) {
        // Error loading saved data - continue with defaults
      }
    }

    if (containerLogistics) {
      loadSavedData()
    }
  }, [containerLogistics, vehiclesCache, usersCache]) // Function to handle saving logistics data
  const handleSaveLogistics = async () => {
    const formValues = getValues()

    // Let's also check the watch value directly
    const watchedTransportedBy = watch('transportedBy')

    const logisticsData: {
      transporterCompanyId?: number | null
      storageCompanyId?: number | null
      vehicleId?: number | null
      transportedById?: number | null
    } = {}

    // Include all fields, even if null, to match the expected API structure
    // Handle both object format and string format for AsyncSelectForm fields
    logisticsData.transporterCompanyId = formValues.transporterCompany
      ? typeof formValues.transporterCompany === 'string'
        ? Number(formValues.transporterCompany)
        : Number(formValues.transporterCompany.id)
      : null
    logisticsData.storageCompanyId = formValues.storageCompany
      ? typeof formValues.storageCompany === 'string'
        ? Number(formValues.storageCompany)
        : Number(formValues.storageCompany.id)
      : null
    logisticsData.vehicleId = formValues.vehicle
      ? typeof formValues.vehicle === 'string'
        ? Number(formValues.vehicle)
        : Number(formValues.vehicle.id)
      : null

    // Priority order for transportedBy value: local state first, then form values, then watched values
    // This ensures we use the most recent selection even if form context loses it
    const transportedByValue =
      localTransportedBy || formValues.transportedBy || watchedTransportedBy

    logisticsData.transportedById = transportedByValue?.id
      ? Number(transportedByValue.id)
      : null

    // DEBUG: Final logisticsData.transportedBy: ${logisticsData.transportedBy}
    // DEBUG: Final logisticsData.transportedBy type: ${typeof logisticsData.transportedBy}
    // DEBUG: END TRANSPORTED BY PROCESSING

    // DEBUG: Final transportedBy value used: ${transportedByValue}
    // DEBUG: Processed logistics data: ${JSON.stringify(logisticsData)}
    // DEBUG: transportedBy processed value: ${logisticsData.transportedBy}
    // DEBUG: END DEBUG

    await updateContainerLogistics.mutateAsync(logisticsData)
  }

  // Expose the save function to the parent component
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveAseguramiento = handleSaveLogistics
      // @ts-ignore - Expose loading state
      window.isSavingAseguramiento = updateContainerLogistics.isPending
    }

    return () => {
      // @ts-ignore
      delete window.handleSaveAseguramiento
      // @ts-ignore
      delete window.isSavingAseguramiento
    }
  }, [isEditing, handleSaveLogistics, updateContainerLogistics.isPending])

  return (
    <div className={'h-[60dvh] overflow-y-auto'}>
      {isLoading && isEditing ? (
        <div className="flex h-full w-full items-center justify-center">
          <SpinnerLoading />
        </div>
      ) : (
        <div
          className={'space-y-5'}
          style={isEditing ? { marginTop: '-.5rem' } : {}}
        >
          {/* Transportista */}
          <div className="flex items-end gap-2">
            <div className="w-full">
              <AsyncSelectForm
                control={control}
                fetcher={fetchTransportCompanies}
                label="Transportista"
                name="transporterCompany"
                notFoundMessage="No se encontraron transportistas."
                placeholder="Buscar transportista..."
              />
            </div>
          </div>

          {/* Almacenaje */}
          <div className="flex items-end gap-2">
            <div className="w-full">
              <AsyncSelectForm
                control={control}
                fetcher={fetchStorageCompanies}
                label="Almacenaje"
                name="storageCompany"
                notFoundMessage="No se encontraron almacenes."
                placeholder="Buscar almacenaje..."
              />
            </div>
          </div>

          {/* Vehículo - Solo se muestra cuando hay transportista seleccionado */}
          {showVehicleAndTransportedBy && (
            <div className="flex items-end gap-2">
              <div className="w-full">
                <AsyncSelectForm
                  control={control}
                  fetcher={fetchVehicles}
                  label="Vehículo"
                  name="vehicle"
                  notFoundMessage="No se encontraron vehículos."
                  placeholder="Buscar vehículo..."
                />
              </div>
            </div>
          )}

          {/* Transportado por - Solo se muestra cuando hay transportista seleccionado */}
          {showVehicleAndTransportedBy && (
            <div className="flex items-end gap-2">
              <div className="w-full">
                <AsyncSelectForm
                  control={control}
                  fetcher={fetchUsers}
                  label="Transportado por"
                  name="transportedBy"
                  placeholder="Seleccionar usuario..."
                />
              </div>
            </div>
          )}

          {/* Información del vehículo - Solo se muestra cuando hay vehículo seleccionado */}
          {selectedVehicle && vehicleInfo && (
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-700">
                Información del vehículo
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label className="text-xs text-gray-500">
                    Identificación
                  </Label>
                  <p className="text-sm font-medium">
                    {vehicleInfo.identification || 'No disponible'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">
                    Número oficial
                  </Label>
                  <p className="text-sm font-medium">
                    {vehicleInfo.oficialNumber || 'No disponible'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tipo</Label>
                  <p className="text-sm font-medium">
                    {vehicleInfo.type || 'No disponible'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
